import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const rules = [
  {
    number: 'I',
    title: 'Health & Safety',
    subtitle: 'The Foundation of Our Collective',
    body: `Every member of the Paw & Co. community - human and animal alike - deserves to feel safe. We require that all pets in our care hold up-to-date vaccination records and a current health certificate issued by a licensed veterinarian. This is not a formality. It is the first act of respect toward every other soul in our collective. Documentation must be presented prior to the first session and renewed annually without exception.`,
    detail: 'Vaccinations required: Rabies, Distemper, Parvovirus, Bordetella. Health certificate valid within 12 months.',
  },
  {
    number: 'II',
    title: 'The Connection Period',
    subtitle: 'Before Care Begins, Understanding Must',
    body: `We do not believe in strangers. Before any service commences, every new client undergoes a mandatory initial consultation - what we call The Connection Period. This is a private meeting between you, your pet, and your assigned caregiver. It is an opportunity to share routines, preferences, sensitivities, and the small details that transform good care into exceptional care. No booking is confirmed until this ritual is complete.`,
    detail: 'Duration: 45–60 minutes. Conducted in-person at the Odesa Atelier or via private video session for international clients.',
  },
  {
    number: 'III',
    title: 'Punctuality',
    subtitle: 'Time Is the Currency of Respect',
    body: `Our caregivers are professionals who manage carefully curated schedules. We ask that you honour the time we have reserved for your pet as you would a private appointment at a fine establishment. Arrivals more than fifteen minutes late without prior notice may result in a shortened session billed at full rate. Cancellations require a minimum of 24 hours' notice. We extend the same courtesy to you - always.`,
    detail: 'Late cancellation policy: Under 24 hours — 50% session fee. No-show — full session fee.',
  },
  {
    number: 'IV',
    title: 'Behavioral Standards',
    subtitle: 'All Personalities Welcome. All Truths Required.',
    body: `We welcome every temperament - the bold, the shy, the exuberant, and the cautious. What we cannot accommodate is surprise. Full and honest disclosure of any aggressive tendencies, fear responses, or behavioral history is not optional - it is a condition of membership. This information allows us to assign the right caregiver, design the right environment, and ensure the safety of every animal and person in our care. Transparency is not a weakness. It is the mark of a responsible owner.`,
    detail: 'Behavioral assessments available upon request. All disclosures are held in strict confidence.',
  },
];

export default function HouseRules() {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.08 }
    );
    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const isVisible = (id: string) => visibleSections.has(id);

  return (
    <div className="bg-cream min-h-screen">
      <Navigation />

      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 md:pt-44 md:pb-24 px-6 md:px-12 lg:px-20">
          <div className="max-w-5xl">
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-forest leading-[0.92] mb-10">
              The House<br />
              <span className="italic">Rules</span>
            </h1>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
              <p className="font-grotesk text-lg text-forest/55 max-w-xl leading-relaxed">
                These are not terms and conditions. They are the values that hold our community together - a quiet agreement between those who take care seriously.
              </p>
            </div>
          </div>
        </section>

        {/* Thin rule */}
        <div className="px-6 md:px-12 lg:px-20">
          <div className="border-t border-forest/15" />
        </div>

        {/* Editorial Photograph */}
        <section
          id="photo"
          data-animate
          className={`px-6 md:px-12 lg:px-20 py-16 md:py-20 transition-all duration-1000 ${isVisible('photo') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <div className="w-full h-[52vh] md:h-[68vh] overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/1f65bef57b9643672cbad222c3f4355a/03f90eb5dd85a9da8b06d6b5ca20e6c5.jpeg"
                  alt="A distinguished dog resting in a private library"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="lg:col-span-5 lg:pb-6">
              <p className="font-grotesk text-xs uppercase tracking-widest text-forest/35 mb-4">The Standard</p>
              <p className="font-serif text-2xl md:text-3xl text-forest leading-snug mb-6">
                "Excellence is not an act.<br />
                <span className="italic">It is a habit.</span>"
              </p>
              <p className="font-grotesk text-sm text-forest/50 leading-relaxed">
                Every rule below exists because we have seen what happens when it is absent. We wrote them not to restrict, but to protect - the animals, the caregivers, and the integrity of what we have built together since 1994.
              </p>
            </div>
          </div>
        </section>

        {/* Thin rule */}
        <div className="px-6 md:px-12 lg:px-20">
          <div className="border-t border-forest/15" />
        </div>

        {/* Rules List */}
        <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
          <div className="space-y-0">
            {rules.map((rule, index) => (
              <div
                key={rule.number}
                id={`rule-${rule.number}`}
                data-animate
                className={`transition-all duration-700 ${isVisible(`rule-${rule.number}`) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 py-16 md:py-20 border-b border-forest/10">
                  {/* Rule number */}
                  <div className="lg:col-span-1 flex lg:flex-col lg:items-start items-center gap-4 lg:gap-0">
                    <span
                      className="font-serif text-forest/15 select-none leading-none"
                      style={{ fontSize: 'clamp(48px, 6vw, 80px)' }}
                    >
                      {rule.number}
                    </span>
                  </div>

                  {/* Title column */}
                  <div className="lg:col-span-3 lg:pt-2">
                    <p
                      className="font-grotesk uppercase text-sienna font-extrabold mb-3"
                      style={{ fontSize: '11px', letterSpacing: '0.2em' }}
                    >
                      {rule.subtitle}
                    </p>
                    <h2 className="font-serif text-2xl md:text-3xl text-forest leading-tight">
                      {rule.title}
                    </h2>
                  </div>

                  {/* Body column */}
                  <div className="lg:col-span-8 lg:pt-2">
                    <p className="font-grotesk text-base text-forest/60 leading-relaxed">
                      {rule.body}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>


      </main>

      <Footer />
    </div>
  );
}
